import { useEffect, useState } from "react";
import { useAuthStore } from "../store/useAuthStore";
import { useProjectStore } from "../store/useProjectStore";
import { Plus } from "lucide-react";
import ProjectForm from "../components/projects/ProjectForm";
import ProjectList from "../components/projects/ProjectList";
import EditProjectModal from "../components/projects/EditProjectModal";
import FinanceProjectsView from "../components/projects/FinanceProjectsView";
import BDProjectsView from "./bd/BDProjectsView";

const ProjectsPage = () => {
  const { authUser } = useAuthStore();
  const { fetchProjects } = useProjectStore();

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editingProject,setEditingProject ] = useState<any>(null);
  // A Draft opens the multi-step creation modal (resume); active projects edit normally.
  const [resumingDraft, setResumingDraft] = useState<any>(null);
  const openEditor = (p: any) => {
    if (p?.isDraft) setResumingDraft(p);
    else setEditingProject(p);
  };
  const closeCreate = () => {
    setDrawerOpen(false);
    setResumingDraft(null);
    fetchProjects(); // reflect any Save-as-Draft done inside the modal
  };

  useEffect(() => {
    if (authUser?._id) fetchProjects();
  }, [authUser]);

  // Finance User gets a read-only, client-free financial view of projects.
  if (authUser?.role === "Finance User") {
    return <FinanceProjectsView />;
  }

  // Business Developer gets the Material 3 view aligned with the BD dashboard.
  if (authUser?.role === "Business Developer") {
    return <BDProjectsView />;
  }

  return (
    <div className="min-h-screen bg-[#f8f9ff] pt-20 px-4 lg:px-8 pb-10">
      <div className="max-w-7xl mx-auto">

        <div className="mb-6">
          <h1 className="text-[26px] font-bold text-slate-900 tracking-tight">
            Manage Projects
          </h1>
          <p className="text-[13px] text-slate-500 mt-1">
            Track and organize your client projects with ease
          </p>
        </div>

        <div className="bg-white rounded-xl border border-slate-200 shadow-[0_4px_6px_-1px_rgba(0,0,0,0.05)] p-6 lg:p-8 relative">

          <div className="flex justify-end mb-8">
            <button
              onClick={() => setDrawerOpen(true)}
              className="group inline-flex items-center gap-3 bg-gradient-to-r from-[#2462b2] to-[#1e55a0] text-white font-bold rounded-2xl px-7 py-4 shadow-xl hover:shadow-2xl hover:scale-105 transition-all duration-300 transform-gpu"
            >
              <Plus size={24} className="group-hover:rotate-90 transition-transform duration-300" />
              <span className="tracking-wide">Add New Project</span>
            </button>
          </div>

          <div className="flex items-center gap-4 mb-8">
            <div className="flex-1 h-px bg-gradient-to-r from-transparent via-gray-300 to-transparent"></div>
            <span className="text-sm font-bold text-gray-500 uppercase tracking-widest">
              All Projects
            </span>
            <div className="flex-1 h-px bg-gradient-to-r from-transparent via-gray-300 to-transparent"></div>
          </div>

          <div className="rounded-2xl border border-gray-100">
           <ProjectList setEditingProject={openEditor} />
          </div>
        </div>

        {/* Project Creation — multi-step MODAL (centered, responsive). Also used
            to RESUME a saved Draft with all previous entries loaded. */}
        {(drawerOpen || resumingDraft) && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-2 sm:p-6" role="dialog" aria-modal="true">
            <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm" onClick={closeCreate} />
            <div className="relative w-full max-w-5xl max-h-[92vh] bg-white rounded-2xl shadow-2xl border border-slate-200/80 flex flex-col overflow-hidden">
              <div className="flex items-center justify-between px-6 sm:px-8 py-5 border-b border-slate-200 shrink-0">
                <div>
                  <h2 className="text-xl font-bold text-slate-900 tracking-tight">
                    {resumingDraft ? "Resume Draft Project" : "Create New Project"}
                  </h2>
                  <p className="text-xs text-slate-500 mt-0.5">Configure client details, project timeline, and operational delivery parameters.</p>
                </div>
                <button onClick={closeCreate} className="w-9 h-9 rounded-lg hover:bg-slate-100 flex items-center justify-center text-slate-400 hover:text-slate-700 transition-colors">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              {/* The form owns the stepper band, the scrolling body and the footer band */}
              <ProjectForm initialProject={resumingDraft} onSuccess={closeCreate} />
            </div>
          </div>
        )}

        <div className="fixed inset-0 z-60 flex items-center justify-center pointer-events-none">
          {editingProject&& (
            <div className="pointer-events-auto">
              <EditProjectModal
                project={editingProject}
                onClose={() => setEditingProject(null)}
              />
            </div>
          )}
        </div>

        <div className="absolute inset-0 -z-10 overflow-hidden pointer-events-none">
          <div className="absolute top-32 -left-40 w-96 h-96 bg-[#2462b2]/5 rounded-full blur-3xl"></div>
          <div className="absolute bottom-32 -right-40 w-96 h-96 bg-[#1e55a0]/5 rounded-full blur-3xl"></div>
        </div>
      </div>
    </div>
  );
};

export default ProjectsPage;
